import { getScrollContainer } from '@theme/scroll-container';

/**
 * Adds a light inertia ease to mouse-wheel/trackpad scrolling on the main page scroll
 * container. Touch scrolling is untouched (touch devices don't fire `wheel` events, so
 * their native momentum scrolling is unaffected). Respects `prefers-reduced-motion` and
 * leaves independently scrollable regions (drawers, dialogs, dropdown panels) to the
 * browser's native handling.
 */

const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

// Quick catch-up rather than long, floaty inertia.
const EASE = 0.18;
const SPEED_MULTIPLIER = 1.25;
const SETTLE_THRESHOLD = 0.5;
const LINE_HEIGHT = 16;

class SmoothWheelScroll {
  /** @type {number | null} */
  #rafId = null;
  #current = 0;
  #target = 0;
  /** @type {EventTarget | null} */
  #lastNestedCheckTarget = null;
  #lastNestedCheckResult = false;

  constructor() {
    this.#syncReducedMotion();
    REDUCED_MOTION_QUERY.addEventListener('change', this.#syncReducedMotion);
  }

  #syncReducedMotion = () => {
    if (REDUCED_MOTION_QUERY.matches) {
      this.#disable();
    } else {
      this.#enable();
    }
  };

  #enable() {
    window.addEventListener('wheel', this.#onWheel, { passive: false });
  }

  #disable() {
    window.removeEventListener('wheel', this.#onWheel);
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
    this.#lastNestedCheckTarget = null;
  }

  /**
   * Whether the wheel event is happening inside a region that scrolls independently of
   * the main page (a drawer, dialog, or dropdown panel), in which case native scrolling
   * should handle it untouched.
   *
   * Wheel events fire many times a second for the same target while the pointer sits
   * still, and walking the ancestor chain with `getComputedStyle` on every one forces a
   * style recalc each time — a major source of scroll jank, especially over deeply
   * nested regions like the header or footer. Cache the result per target so repeat
   * events during one continuous scroll are free.
   *
   * @param {EventTarget | null} target
   * @param {Element} mainContainer
   * @returns {boolean}
   */
  #isNestedScrollTarget(target, mainContainer) {
    if (target === this.#lastNestedCheckTarget) return this.#lastNestedCheckResult;

    let node = target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
    let result = false;

    while (node && node !== mainContainer && node !== document.body) {
      const style = getComputedStyle(node);
      const scrollsVertically =
        (style.overflowY === 'auto' || style.overflowY === 'scroll') && node.scrollHeight > node.clientHeight;

      if (scrollsVertically) {
        result = true;
        break;
      }

      node = node.parentElement;
    }

    this.#lastNestedCheckTarget = target;
    this.#lastNestedCheckResult = result;

    return result;
  }

  /** @param {WheelEvent} event */
  #onWheel = (event) => {
    // Leave pinch-zoom and horizontal-only gestures to the browser.
    if (event.ctrlKey || event.deltaY === 0) return;

    // A drawer/dialog is open and scroll-locking the page; let it handle its own scroll.
    if (document.documentElement.hasAttribute('scroll-lock')) return;

    const container = getScrollContainer();
    if (!(container instanceof HTMLElement)) return;

    if (this.#isNestedScrollTarget(event.target, container)) return;

    event.preventDefault();

    if (this.#rafId === null) {
      // Resync with any scrolling that happened while we weren't animating (keyboard,
      // scrollbar drag, an anchor jump).
      this.#current = container.scrollTop;
      this.#target = this.#current;
    }

    const delta =
      (event.deltaMode === 1
        ? event.deltaY * LINE_HEIGHT
        : event.deltaMode === 2
          ? event.deltaY * container.clientHeight
          : event.deltaY) * SPEED_MULTIPLIER;

    // Accumulate unclamped here; clamping against scrollHeight/clientHeight happens once
    // per animation frame in #tick instead of on every wheel event, since that read can
    // force a synchronous layout recalc and wheel events can fire far more often than
    // once per frame.
    this.#target += delta;

    if (this.#rafId === null) {
      this.#rafId = requestAnimationFrame(() => this.#tick(container));
    }
  };

  /** @param {HTMLElement} container */
  #tick(container) {
    const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
    this.#target = Math.min(Math.max(this.#target, 0), maxScroll);

    this.#current += (this.#target - this.#current) * EASE;

    if (Math.abs(this.#target - this.#current) < SETTLE_THRESHOLD) {
      this.#current = this.#target;
      container.scrollTo({ top: this.#current, behavior: 'instant' });
      this.#rafId = null;
      return;
    }

    container.scrollTo({ top: this.#current, behavior: 'instant' });
    this.#rafId = requestAnimationFrame(() => this.#tick(container));
  }
}

new SmoothWheelScroll();
